import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { createCheckoutSession } from '../api/checkout';
import { getPromotionCode } from '../api/coupon';
import { getPlanTerms } from '../api/plan';
import { NoticeModal } from '../components/Modals';
import UserPageLayout from '../components/UserPageLayout';
import { termsAgreementSchema } from '../schemas/termsAgreementSchema';
import { setCampaignCode } from '../utils/storage.js';
import { usePlans } from '../utils/usePlans';

const TermsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeMsg, setNoticeMsg] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [termsData, setTermsData] = useState({ general: '', coupon: '' });
  const [termsLoading, setTermsLoading] = useState(true);
  const [memberId, setMemberId] = useState(null);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const companyIdFromUrl = searchParams.get('company_id');
    const priceIdFromUrl = searchParams.get('price_id');
    const userIdFromUrl = searchParams.get('user_id');
    setMemberId(searchParams.get('member_id'));

    if (
      !location.state &&
      companyIdFromUrl &&
      priceIdFromUrl &&
      userIdFromUrl
    ) {
      navigate(location.pathname, {
        replace: true,
        state: {
          companyId: companyIdFromUrl,
          price_id: priceIdFromUrl,
          userId: userIdFromUrl,
        },
      });
    }
  }, [searchParams]);

  const showNotice = (msg) => {
    setNoticeMsg(msg);
    setNoticeOpen(true);
  };

  // location.stateから取得を試み、存在しない場合はsessionStorageやトークンから取得
  const { userId, companyId, price_id } = location.state || {};

  const {
    subscriptionOptions,
    oneTimeOptions,
    loading: plansLoading,
  } = usePlans();

  const selectedPlan =
    subscriptionOptions.find((p) => p.price_id === price_id) ||
    oneTimeOptions.find((p) => p.price_id === price_id);
  const planId = selectedPlan?.id ?? null;
  const hasCoupon = selectedPlan?.promotion_code;
  const isAutoCouponPrefill = Boolean(selectedPlan?.is_auto_coupon_prefill);

  // planIdをもとに利用規約を取得（TermsEdit と同様にプラン別の規約を表示）
  useEffect(() => {
    if (!planId) {
      // プラン未選択 or プラン読み込み前は規約を取得しない
      if (!plansLoading) {
        setTermsLoading(false);
      }
      return;
    }
    const loadTerms = async () => {
      setTermsLoading(true);
      try {
        const response = await getPlanTerms(planId);
        if (response.success) {
          setTermsData({
            general: response.general ?? '',
            coupon: response.coupon ?? '',
          });
        } else {
          console.error('Failed to load terms:', response.message);
          setTermsData({ general: '', coupon: '' });
        }
      } catch (error) {
        console.error('Error loading terms:', error);
        setTermsData({ general: '', coupon: '' });
      } finally {
        setTermsLoading(false);
      }
    };
    loadTerms();
  }, [planId, plansLoading]);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = useForm({
    resolver: zodResolver(termsAgreementSchema),
    defaultValues: {
      agreed: false,
    },
  });

  const agreed = watch('agreed');

  // クーポン自動フィルインが有効な場合、クーポンコードを自動で設定
  useEffect(() => {
    if (!selectedPlan) {
      setCouponCode('');
      return;
    }

    const isPrefillEnabled = Boolean(selectedPlan?.is_auto_coupon_prefill);
    if (!isPrefillEnabled) {
      setCouponCode('');
      return;
    }

    const linkedCouponCode = selectedPlan?.coupon?.coupon_code ?? '';
    setCouponCode(linkedCouponCode);
  }, [selectedPlan]);

  const onSubmit = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showNotice('ログインしてください');
      navigate('/');
      return;
    }

    if (!companyId || !price_id) {
      showNotice('会社情報が正しく設定されていません');
      return;
    }

    // subscriptionOptionsが読み込まれているか確認
    if (plansLoading) {
      showNotice(
        'プラン情報を読み込み中です。少し待ってから再度実行してください。',
      );
      return;
    }

    // 価格IDがサブスクか判定
    const mode = subscriptionOptions.some((sub) => sub.price_id === price_id)
      ? 'subscription'
      : 'payment';
    const trialDays =
      subscriptionOptions.find((sub) => sub.price_id === price_id)
        ?.trial_days || null;

    let promotionCode = null;
    let redeemBy = null;
    if (couponCode) {
      const res = await getPromotionCode(couponCode);
      if (!res || !res.promotion_code) {
        showNotice('クーポンコードが存在しません');
        return;
      }
      promotionCode = res.promotion_code;
      redeemBy = res.redeem_by;
    }

    const redeemByISO = redeemBy
      ? new Date(redeemBy.replace(' ', 'T') + '+09:00')
      : null;
    const now = Date.now();

    if (promotionCode) {
      if (
        promotionCode === selectedPlan?.promotion_code &&
        (!redeemByISO || new Date(redeemByISO).getTime() > now)
      ) {
        try {
          const res = await createCheckoutSession(
            companyId,
            price_id,
            mode,
            trialDays,
            null,
            userId,
            promotionCode,
            couponCode,
            memberId ?? null,
          );

          // チェックアウトセッション作成成功時にcampaignCodeをクリア
          setCampaignCode(null);

          window.location.href = res.checkout_url;
        } catch (err) {
          console.error('契約処理に失敗', err);
          showNotice('契約処理に失敗しました');
        }
      } else if (
        promotionCode === selectedPlan?.promotion_code &&
        new Date(redeemByISO).getTime() < now
      ) {
        showNotice('有効期間外です');
      } else {
        showNotice('こちらのクーポンコードは利用できません');
      }
    } else {
      try {
        const res = await createCheckoutSession(
          companyId,
          price_id,
          mode,
          trialDays,
          null,
          userId,
          promotionCode ?? null,
          couponCode ?? null,
          memberId ?? null,
        );

        // チェックアウトセッション作成成功時にcampaignCodeをクリア
        setCampaignCode(null);

        window.location.href = res.checkout_url;
      } catch (err) {
        console.error('契約処理に失敗', err);
        showNotice('契約処理に失敗しました');
      }
    }
  };

  const parseTermsContent = (content) => {
    if (!content) return null;

    const parseTermsText = (text) => {
      const sections = [];
      const lines = text.split('\n');

      let currentSection = null;

      lines.forEach((line) => {
        const trimmedLine = line.trim();

        // 空行はスキップ
        if (trimmedLine === '') {
          return;
        }

        // より詳細な判定条件
        const isBulletPoint =
          trimmedLine.startsWith('・') ||
          trimmedLine.startsWith('※') ||
          trimmedLine.startsWith('○') ||
          trimmedLine.startsWith('●');

        const isNumberedItem = /^\d+\./.test(trimmedLine); // 1. 2. などの番号付きリスト

        // セクションタイトルの判定条件を強化
        const isSectionTitle =
          !isBulletPoint &&
          !isNumberedItem &&
          !trimmedLine.includes('。') &&
          (trimmedLine.length <= 50 ||
            trimmedLine.includes('第') ||
            trimmedLine.startsWith('■') ||
            trimmedLine.startsWith('▼') ||
            trimmedLine.endsWith('について') ||
            trimmedLine.endsWith('に関して'));

        if (isSectionTitle) {
          // 新しいセクションのタイトルとして扱う
          if (currentSection) {
            sections.push(currentSection);
          }

          currentSection = {
            title: trimmedLine,
            items: [],
            type: 'section',
          };
        } else {
          // それ以外は全てitemsに追加
          if (currentSection) {
            currentSection.items.push({
              text: trimmedLine,
              type: isBulletPoint
                ? 'bullet'
                : isNumberedItem
                  ? 'numbered'
                  : 'normal',
            });
          } else {
            // セクションタイトルがない場合は、デフォルトセクションを作成
            currentSection = {
              title: '',
              items: [
                {
                  text: trimmedLine,
                  type: isBulletPoint
                    ? 'bullet'
                    : isNumberedItem
                      ? 'numbered'
                      : 'normal',
                },
              ],
              type: 'section',
            };
          }
        }
      });

      // 最後のセクションを追加
      if (currentSection) {
        sections.push(currentSection);
      }

      return sections;
    };

    const sections = parseTermsText(content);

    return sections.map((section, sectionIndex) => (
      <Box key={sectionIndex} sx={{ mb: 2 }}>
        {section.title && (
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'bold',
              fontSize: '1.2rem',
              color: 'primary.main',
              mb: 0.5,
              pl: 2,
              py: 1,
              borderRadius: '0 4px 4px 0',
            }}
          >
            {section.title}
          </Typography>
        )}

        <Box sx={{ pl: section.title ? 1 : 0 }}>
          {section.items.map((item, itemIndex) => {
            const styles = {
              bullet: {
                fontSize: '0.9rem',
                lineHeight: 1.6,
                mb: 0.5,
                pl: 2,
                position: 'relative',
              },
              numbered: {
                fontSize: '0.9rem',
                lineHeight: 1.6,
                mb: 0.5,
                pl: 1,
                fontWeight: 'medium',
              },
              normal: {
                fontSize: '0.9rem',
                lineHeight: 1.6,
                mb: 1,
                textAlign: 'justify',
              },
            };

            return (
              <Typography
                key={itemIndex}
                variant="body2"
                sx={styles[item.type]}
              >
                {item.text}
              </Typography>
            );
          })}
        </Box>
      </Box>
    ));
  };

  if (plansLoading || termsLoading) {
    return (
      <UserPageLayout title="利用規約" maxWidth="md">
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1">プラン情報を読み込み中...</Typography>
        </Box>
      </UserPageLayout>
    );
  }

  return (
    <UserPageLayout title="利用規約" maxWidth="md">
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        {hasCoupon && (
          <>
            <Typography>クーポンコード:</Typography>
            <TextField
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="クーポンコードを入力"
              size="small"
              disabled={isAutoCouponPrefill}
              sx={{ flex: 1, paddingTop: '2px' }}
            />
          </>
        )}
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          maxWidth: 'none',
          boxShadow: 'none',
          minHeight: 'fit-content',
          padding: 0,
          mt: '5dvh',
          alignItems: 'flex-start',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            maxWidth: '900px',
            width: '100%',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              利用規約
            </Typography>
            <Box
              sx={{
                border: '1px solid #ccc',
                borderRadius: 1,
                p: 2,
                minHeight: '600px',
                maxHeight: '600px',
                overflow: 'auto',
                backgroundColor: '#fafafa',
                width: '100%',
              }}
            >
              {parseTermsContent(termsData.general)}
            </Box>
          </Box>

          {hasCoupon && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                クーポン利用規約
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  p: 2,
                  minHeight: '600px',
                  maxHeight: '600px',
                  overflow: 'auto',
                  backgroundColor: '#fafafa',
                  width: '100%',
                }}
              >
                {parseTermsContent(termsData.coupon)}
              </Box>
            </Box>
          )}
        </Paper>

        <Box
          sx={{
            my: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  {...register('agreed')}
                  checked={agreed}
                  color="primary"
                />
              }
              label={
                hasCoupon
                  ? '利用規約/クーポン利用規約に同意します'
                  : '利用規約に同意します'
              }
            />
          </Box>
        </Box>

        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/mypage')}
            sx={{ borderRadius: 1, flex: 1 }}
            disabled={isSubmitting}
          >
            戻る
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting || !agreed}
            sx={{ borderRadius: 1, flex: 1 }}
          >
            {isSubmitting ? '処理中...' : '同意して決済画面に進む'}
          </Button>
        </Stack>
      </Box>
      <NoticeModal
        open={noticeOpen}
        message={noticeMsg}
        onClose={() => setNoticeOpen(false)}
      />
    </UserPageLayout>
  );
};

export default TermsPage;
